import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { presupuestoProformaService } from '../services/presupuestoProformaService';
import type {
  PresupuestoProformaDatosFormData,
  PresupuestosProformaFiltros,
} from '../types';

export const PRESUPUESTOS_PROFORMA_QUERY_KEY = ['presupuestos', 'proforma'] as const;

export const listadoQueryKey = (filtros: PresupuestosProformaFiltros) =>
  [
    ...PRESUPUESTOS_PROFORMA_QUERY_KEY,
    'list',
    filtros.search ?? null,
    filtros.estado ?? null,
    filtros.desde ?? null,
    filtros.hasta ?? null,
    filtros.importado ?? null,
    filtros.page ?? 1,
  ] as const;

/** Listado de presupuestos independientes con filtros. */
export const usePresupuestosProforma = (filtros: PresupuestosProformaFiltros = {}) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: listadoQueryKey(filtros),
    queryFn: () => presupuestoProformaService.listar(filtros),
  });

  const invalidar = () =>
    queryClient.invalidateQueries({ queryKey: PRESUPUESTOS_PROFORMA_QUERY_KEY });

  const crearMutation = useMutation({
    mutationFn: (
      payload: PresupuestoProformaDatosFormData & { nombre: string; plantilla_id?: string | null }
    ) =>
      presupuestoProformaService.crear(payload),
    onSuccess: invalidar,
  });

  const eliminarMutation = useMutation({
    mutationFn: (id: string) => presupuestoProformaService.eliminar(id),
    onSuccess: invalidar,
  });

  return {
    presupuestos: data?.data ?? [],
    meta: data?.meta,
    isLoading,
    error,
    crearPresupuesto: crearMutation.mutateAsync,
    eliminarPresupuesto: eliminarMutation.mutateAsync,
    isCreando: crearMutation.isPending,
    isEliminando: eliminarMutation.isPending,
  };
};
